import type { CSSProperties } from 'react';

import type { BevelWrapperAnimationProps } from './BevelInsetWrapper.types';
import type { ButtonAnimationEasing } from './Button.types';
import type { UseButtonAnimationArgs } from './useButtonAnimation.types';

export type { UseButtonAnimationArgs };

export interface UseButtonAnimationResult {
  containerClassName: string;
  containerStyle?: CSSProperties;
  /** Ready-to-spread slice for `<BevelInsetWrapper>`. */
  bevelWrapperProps: BevelWrapperAnimationProps;
}

const DEFAULT_DURATION_MS = 150;
const DEFAULT_EASING: ButtonAnimationEasing = 'ease-out';

const CSS_EASING: Record<ButtonAnimationEasing, string> = {
  linear: 'linear',
  'ease-out': 'cubic-bezier(0, 0, 0.2, 1)',
  'ease-in-out': 'cubic-bezier(0.4, 0, 0.2, 1)',
};

/**
 * Format `[x1, y1, x2, y2]` as the CSS `cubic-bezier(...)` string. Clamps
 * the x components to `[0, 1]` (CSS spec); y components may overshoot for
 * back-out / spring curves and are passed through unchanged.
 */
function formatCubicBezier(p: readonly [number, number, number, number]): string {
  const [x1, y1, x2, y2] = p;
  const clampX = (v: number) => Math.min(1, Math.max(0, v));
  return `cubic-bezier(${clampX(x1)}, ${y1}, ${clampX(x2)}, ${y2})`;
}

/**
 * Web Button animation hook. Emits CSS-class outputs + optional inline
 * `transition-duration` / `transition-timing-function` overrides. The
 * container variant base already declares `web:transition-[transform,colors,
 * box-shadow,opacity] web:duration-150 web:ease-out web:enabled:active:scale-[0.96]`,
 * so the default path is zero-cost (no inline style). The bevel wrapper
 * carries `transition-opacity duration-150 ease-out group-active:opacity-0`
 * for a smooth fade as the Pressable enters `:active`.
 *
 * `disableAnimation` short-circuits everything to `transition: none`.
 */
export function useButtonAnimation(
  args: UseButtonAnimationArgs,
): UseButtonAnimationResult {
  const { animationDuration, animationEasing, animationBezier, disableAnimation } = args;

  if (disableAnimation) {
    return {
      containerClassName: '',
      containerStyle: { transition: 'none' },
      bevelWrapperProps: {
        animationClassName: '',
        webAnimationStyle: { transition: 'none' },
      },
    };
  }

  const usingDefaults =
    animationDuration === undefined &&
    animationEasing === undefined &&
    animationBezier === undefined;

  if (usingDefaults) {
    return {
      containerClassName: '',
      bevelWrapperProps: {
        animationClassName:
          'transition-opacity duration-150 ease-out group-active:opacity-0',
      },
    };
  }

  const duration = animationDuration ?? DEFAULT_DURATION_MS;
  // Bezier wins over named easing — matches the Button.types contract.
  const transitionTimingFunction = animationBezier
    ? formatCubicBezier(animationBezier)
    : CSS_EASING[animationEasing ?? DEFAULT_EASING];

  const transitionStyle: CSSProperties = {
    transitionDuration: `${duration}ms`,
    transitionTimingFunction,
  };

  return {
    containerClassName: '',
    containerStyle: transitionStyle,
    bevelWrapperProps: {
      animationClassName: 'transition-opacity group-active:opacity-0',
      webAnimationStyle: transitionStyle,
    },
  };
}
